import { useUploadMentorCv } from '@actions';
import { ReactComponent as CvIcon } from '@assets/icons/profile-icons/work-icon.svg';
import Button from '@components/V4/Button';
import useToast from 'apps/agora/src/hooks/useToast';
import { downloadMentorCv } from 'apps/agora/src/utils/downloadMentorCv';
import { useRef, useState } from 'react';
import { useQueryClient } from 'react-query';

interface ProfileAsideMentorCvProps {
  hasCv: boolean;
  userId: string;
  hasEditPermissions: boolean;
}

const ProfileAsideMentorCv = (props: ProfileAsideMentorCvProps) => {
  const { hasCv, userId, hasEditPermissions } = props;
  const toast = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const { mutate: uploadCv, isLoading: isUploading } = useUploadMentorCv({
    onSuccess: async () => {
      toast.success('CV uploaded successfully.');
      await queryClient.invalidateQueries('/users/:id/profile');
    },
    onError: () => {
      toast.error('CV could not be uploaded.');
    },
  });

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('cv', file);
    uploadCv(formData);
    e.target.value = '';
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      await downloadMentorCv(userId);
    } catch {
      toast.error('CV could not be downloaded.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex gap-6 py-4 px-6">
      <div className="flex gap-4 w-full">
        <CvIcon className="min-w-4 min-h-4 w-4 h-4 text-customPrimary" />
        <div className="flex flex-col gap-4 w-full">
          <h3 className="font-raleway font-bold">CV</h3>
          <div className="flex flex-wrap items-center gap-3">
            {hasCv ? (
              <Button
                buttonText="Download CV"
                variant="secondary"
                isLoading={isDownloading}
                onClick={handleDownload}
              />
            ) : (
              <span className="text-customGrey text-sm">No CV uploaded</span>
            )}
            {hasEditPermissions && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="hidden"
                  onChange={onFileChange}
                />
                <Button
                  buttonText={hasCv ? 'Replace CV' : 'Upload CV'}
                  variant="primary"
                  isLoading={isUploading}
                  onClick={() => fileInputRef.current?.click()}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileAsideMentorCv;
